import { CHAPTERS } from '../data/site';
import ChapterShell from '../components/ChapterShell';
import { MaskedLines, FadeUp, Eyebrow } from '../components/Reveal';

const C = CHAPTERS[0];
const RED = '#B50A00';

const TIMELINE = [
  { n: '01', t: 'Grounding', d: 'Established project intent and defined the core problem domain.', m: 'Dec’ 25' },
  { n: '02', t: 'Validation', d: 'Tested the premise against early signals from the field.', m: 'Dec’ 25' },
  { n: '03', t: 'Architecture', d: 'Designed the project approach, execution frameworks and artefacts.', m: 'Jan’ 26' },
  { n: '04', t: 'Deconstruction', d: 'Broke the retail ecosystem into formats, workflows and forces.', m: 'Jan’ 26' },
  { n: '05', t: 'Immersion', d: 'On ground study of operations, behaviours and decision dynamics.', m: 'Feb’ 26' },
  { n: '06', t: 'Framing', d: 'Initial synthesis, structural understanding and contextual narratives.', m: 'Feb’ 26' },
  { n: '07', t: 'Probing', d: 'Primary research round two: daily operations, decision logic, risk perception.', m: 'Mar’ 26' },
  { n: '08', t: 'Synthesis', d: 'Clustered data, mapped patterns, extracted system level insights.', m: 'Mar’ 26' },
  { n: '09', t: 'Definition', d: 'Defined and articulated the problem with clarity and direction.', m: 'Apr’ 26' },
  { n: '10', t: 'Materialization', d: 'Ideation, concept development and visualization into tangible outputs.', m: 'Apr’ 26' },
];

const Genesis = () => (
  <ChapterShell chapter={C} heroImg="/assets/book/new-photo3.jpg" heroCaption="The neighbourhood provision store, where it all begins.">

    {/* PREMISE */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="genesis-premise">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Eyebrow color={RED}>01 · Project Premise</Eyebrow>
        </div>
        <div className="md:col-span-8 space-y-8">
          <FadeUp>
            <p className="text-2xl md:text-4xl font-light tracking-tight leading-[1.15] text-ink">
              Grocery may sound like an easy business, <span style={{ color: RED }}>but it is not.</span> I am sure of that.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-base md:text-lg text-ink/70 leading-relaxed max-w-2xl">
              India has always been a land of trading and business operations, and at the core of it
              lies offline channels of business. The biggest forefront of these trade practices is held
              by kirana shops, an ecosystem so widespread and unique to India. In the past few years,
              constant change, and the increasing speed of it, has had a critical impact on these stores,
              an impact so big it may be one of the biggest threats they have faced in their entire history.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-base md:text-lg text-ink/70 leading-relaxed max-w-2xl">
              The nature of competition has completely changed. Where competition used to be another
              human mind, this time it is data backed, technologically advanced, systemized, and
              automated processes. This project is an attempt to understand this shift and respond
              to it with a structured approach.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* SCOPE QUOTE */}
    <section className="relative overflow-hidden px-5 md:px-10 lg:px-24 py-24 md:py-36" style={{ background: RED }} data-testid="genesis-scope">
      <div className="bandhani-dot absolute inset-0 text-paper/[0.08] pointer-events-none" />
      <Eyebrow color="#FAFAF7">Project Scope, The How Might We</Eyebrow>
      <MaskedLines
        lines={['"How might we design a system', "for India's small grocery retailers", 'that helps them survive current', 'operational pressures, and build', 'the capability to grow?"']}
        className="relative mt-10 text-3xl md:text-6xl font-light tracking-tighter leading-[1.05] text-paper"
      />
      <FadeUp delay={0.25}>
        <p className="relative mt-10 text-paper/75 text-base md:text-lg leading-relaxed max-w-2xl">
          A system focused on simple, scalable services and supporting products that work together
          as one ecosystem, making everyday decision making and operational control easier, while
          preparing retailers for future expansion within a changing retail landscape.
        </p>
      </FadeUp>
    </section>

    {/* FOCUS AREAS */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="genesis-focus">
      <Eyebrow color={RED}>Focus Areas</Eyebrow>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/15 border border-ink/15">
        {[
          'Small, family run grocery retailers (general trade)',
          'Daily store operations and decision making practices',
          'In shop workflows and operational control',
        ].map((t, i) => (
          <FadeUp key={i} delay={i * 0.07} className="bg-paper p-8 md:p-12">
            <p className="text-5xl font-light tracking-tighter" style={{ color: RED }}>0{i + 1}</p>
            <p className="mt-6 text-lg md:text-xl font-semibold leading-snug">{t}</p>
          </FadeUp>
        ))}
      </div>

      <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <Eyebrow color={RED}>Key Domains Covered</Eyebrow>
          <ul className="mt-8 divide-y divide-ink/10 border-y border-ink/10">
            {[
              'Inventory awareness and stock related decisions',
              'Purchase planning and supplier interaction logic',
              'Credit handling and customer relationship dynamics',
              'Role distribution within family run stores',
              'Sales tracking and day-to-day transaction flow',
              'Frontend and backend operational disconnect',
            ].map((t, i) => (
              <FadeUp key={i} delay={i * 0.04} y={16}>
                <li className="flex items-baseline gap-6 py-5">
                  <span className="text-xs font-medium tracking-widest" style={{ color: RED }}>{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-base md:text-lg font-medium">{t}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
        </div>
        <div>
          <Eyebrow color={RED}>Design Boundaries</Eyebrow>
          <ul className="mt-8 divide-y divide-ink/10 border-y border-ink/10">
            {[
              'Designed for low capital, high constraint environments',
              'Builds on existing practices instead of replacing them',
              'Focus on enabling, not automating everything',
              'Prioritises adoption over complexity',
            ].map((t, i) => (
              <FadeUp key={i} delay={i * 0.04} y={16}>
                <li className="flex items-baseline gap-6 py-5">
                  <span className="text-xs font-medium tracking-widest" style={{ color: RED }}>{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-base md:text-lg font-medium">{t}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
          <FadeUp delay={0.2}>
            <div className="mt-10 border-l-4 pl-6 py-2" style={{ borderColor: RED }}>
              <p className="text-xs uppercase tracking-[0.3em] text-support mb-3">System Direction</p>
              <p className="text-base md:text-lg font-medium leading-relaxed">
                A product led service system with a centralised in shop operational layer,
                modular and scalable, with a low learning curve and minimal behavioural
                disruption, moving gradually toward structured, data-supported operations.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* DESIGN BRIEF */}
    <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="genesis-brief">
      <Eyebrow color="#F9526B">The Design Brief</Eyebrow>
      <FadeUp delay={0.1}>
        <p className="mt-10 text-2xl md:text-4xl font-medium leading-[1.3] tracking-tight max-w-5xl text-paper/90">
          Design a <span className="text-brand-pink">product led service system</span> to structure fragmented,
          daily, repetitive tasks and management practices of a retail grocery store into a
          <span className="text-brand-pink"> flexible, unified operational layer</span>, reducing cognitive load
          and decision fatigue through a centralised data-recording mechanism and modular,
          expandable micro-services, while building readiness for
          <span className="text-brand-pink"> seamless omnichannel operations</span>.
        </p>
      </FadeUp>
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/15 pt-10">
        {[
          'Grow into a venture level scalable entity',
          'Bridge traditional retail and digital systems',
          'Become a daily use operating system for small retail',
          'Expand as a modular system across stores and regions',
        ].map((t, i) => (
          <FadeUp key={i} delay={i * 0.06}>
            <p className="text-2xl font-light text-brand-pink">0{i + 1}</p>
            <p className="mt-3 text-sm text-paper/70 leading-relaxed">{t}</p>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* TIMELINE */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="genesis-timeline">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <Eyebrow color={RED}>Methodology · Timeline</Eyebrow>
          <MaskedLines lines={['Twenty weeks,', 'ten phases.']} className="mt-8 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]" />
        </div>
        <FadeUp className="text-sm uppercase tracking-[0.25em] text-support">
          Dec 2025 → Apr 2026
        </FadeUp>
      </div>
      <div className="mt-14 flex gap-6 overflow-x-auto no-scrollbar pb-6 -mx-5 px-5 md:mx-0 md:px-0" data-testid="timeline-track">
        {TIMELINE.map((p, i) => (
          <FadeUp key={p.n} delay={i * 0.05} className="shrink-0 w-64 md:w-72">
            <div className="group border border-ink/15 p-7 h-full transition-colors duration-500 hover:bg-brand-red hover:text-paper hover:border-brand-red">
              <div className="flex items-baseline justify-between">
                <span className="text-4xl font-light tracking-tighter opacity-25">{p.n}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] opacity-60">{p.m}</span>
              </div>
              <p className="mt-8 text-xl font-medium tracking-tight">{p.t}</p>
              <p className="mt-3 text-sm leading-relaxed opacity-70">{p.d}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  </ChapterShell>
);

export default Genesis;
